<script setup lang="ts">
/**
 * Действие из расшифровки — карточка 12-6 (эпик DEV-2088, решение 5 гриля).
 *
 * Модель предложила, решает человек: задачу заводит кнопка, а не разбор. Своей
 * кнопки «В задачи» у карточки нет — ассистенту тут нечего разбирать, действие
 * уже атомарно, и кнопка ведёт прямо в форму задачи с проектом встречи.
 *
 * Отклонение обратимо: случайный клик иначе уносил бы действие ещё и из
 * переноса в следующий созвон серии, где о нём напомнили бы.
 */
import { useToast } from '@vinprop/design-system'
import { Check, Copy, ExternalLink, Sparkles, Undo2, X } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { formatDueDate } from '@/features/issues/format'

import { useNotesStore } from '../notesStore'

import type { Note } from '../notes'

const props = defineProps<{
  note: Note
  /** Проект встречи — предзаполняет форму задачи */
  projectId: string
}>()

const { t, locale } = useI18n()
const router = useRouter()
const notes = useNotesStore()
const toast = useToast()

const state = computed(() => props.note.ai?.state ?? 'pending')
const keys = computed(() => props.note.ai?.issueKeys ?? [])

const due = computed(() =>
  props.note.ai?.dueDate ? formatDueDate(props.note.ai.dueDate, locale.value) : '',
)

/**
 * Форма задачи — новой вкладкой, как и у обычной заметки: экран встречи в этот
 * момент читают дальше, а форма занимает его целиком.
 *
 * Состояние карточки ставит уже сама форма, когда задача заведена: обещать
 * «принято» до того, как задача появилась, значит соврать при первой же
 * отменённой форме.
 */
const toIssue = () => {
  const href = router.resolve({
    name: 'plan',
    query: { note: props.note.id, project: props.projectId || undefined },
  }).href

  window.open(href, '_blank', 'noopener')
}

/**
 * Ссылка на заведённую задачу — абсолютная: её копируют, чтобы кинуть в чат,
 * а путь без домена там не открывается.
 */
const linkTo = (key: string) => `${window.location.origin}/issue/${key}`

const copyLink = async (key: string) => {
  try {
    await navigator.clipboard.writeText(linkTo(key))
    toast.success(t('meetings.ai.linkCopied', { key }))
  } catch {
    // Буфер живёт только в безопасном контексте и умеет отказывать молча.
    // Показать «скопировано» при пустом буфере — худшее из возможного:
    // человек уйдёт вставлять то, чего там нет.
    toast.error(t('meetings.ai.copyFailed'))
  }
}
</script>

<template>
  <article class="action" :class="`action--${state}`">
    <p class="action__body">{{ note.body }}</p>

    <div class="action__meta">
      <span class="action__ai">
        <Sparkles :size="11" aria-hidden="true" />
        {{ t('meetings.ai.badge') }}
      </span>

      <span v-if="note.ai?.assigneeName" class="action__chip">{{ note.ai.assigneeName }}</span>
      <span v-if="due" class="action__chip">{{ due }}</span>

      <template v-if="state === 'accepted'">
        <span class="action__done">
          <Check :size="12" aria-hidden="true" />
          {{ t('meetings.ai.accepted') }}
        </span>

        <!-- Задача открывается соседней вкладкой: действия разбирают списком,
             и уход со страницы стоит возврата и поиска места, где остановился.
             Ссылкой, а не кнопкой с window.open — иначе средний клик и Ctrl
             перестают работать так, как везде. -->
        <span v-for="key in keys" :key="key" class="action__issue">
          <RouterLink class="action__key" :to="`/issue/${key}`" target="_blank" rel="noopener">
            {{ key }}
          </RouterLink>

          <button
            type="button"
            class="action__copy"
            :aria-label="t('meetings.ai.copyLink', { key })"
            :title="t('meetings.ai.copyLink', { key })"
            @click="copyLink(key)"
          >
            <Copy :size="11" aria-hidden="true" />
          </button>
        </span>
      </template>

      <template v-else-if="state === 'rejected'">
        <span class="action__rejected">{{ t('meetings.ai.rejected') }}</span>

        <button type="button" class="action__tool" @click="notes.setAiState(note.id, 'pending')">
          <Undo2 :size="12" aria-hidden="true" />
          {{ t('meetings.ai.undo') }}
        </button>
      </template>

      <template v-else>
        <button type="button" class="action__tool action__tool--main" @click="toIssue()">
          <ExternalLink :size="12" aria-hidden="true" />
          {{ t('meetings.ai.toIssue') }}
        </button>

        <button type="button" class="action__tool" @click="notes.setAiState(note.id, 'rejected')">
          <X :size="12" aria-hidden="true" />
          {{ t('meetings.ai.reject') }}
        </button>
      </template>
    </div>
  </article>
</template>

<style scoped>
.action {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

/* Отклонённое остаётся на экране приглушённым: вернуть его можно только там,
   где оно видно. */
.action--rejected {
  opacity: 0.6;
}

.action__body {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  line-height: 20px;
  white-space: pre-wrap;
}

.action__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.action__ai {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px;
  border-radius: var(--radius-pill, 999px);
  background: var(--color-primary-tint);
  color: var(--color-primary);
  font-size: var(--font-size-overline);
  line-height: 18px;
}

.action__chip {
  padding: 0 6px;
  border-radius: var(--radius-pill, 999px);
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-size: var(--font-size-overline);
  line-height: 18px;
}

.action__done,
.action__rejected {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  line-height: 18px;
}

.action__issue {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.action__key {
  color: var(--color-primary);
  font-size: var(--font-size-overline);
  line-height: 18px;
  text-decoration: none;
}

.action__key:hover {
  text-decoration: underline;
}

.action__copy {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.action__copy:hover {
  color: var(--color-primary);
}

/* Кнопки видны всегда, без наведения: на телефоне наводить нечем, а разбирают
   действия чаще всего именно с него, сразу после созвона. */
.action__tool {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: inherit;
  font-size: var(--font-size-overline);
  line-height: 18px;
  cursor: pointer;
}

.action__tool--main {
  color: var(--color-primary);
}

.action__tool:hover {
  color: var(--color-text-primary);
}
</style>
